import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../../global.css';
import Navbar from '../../components/Navbar';
import SiteFooter from '../../components/SiteFooter';
export default function Developers() {
  return (
    <>
      <div className="atm-grid" aria-hidden="true" />
      <div className="atm-noise" aria-hidden="true" />
      <div className="wrap">
        <Navbar />
        <DevHero />
        <QuickStart />
        <ApiReference />
        <TechnicalCapabilities />
        <RegionsAndLatency />
        <TechnicalPricing />
        <DevCTA />
        <SiteFooter />
      </div>
    </>
  );
}

function DevHero() {
  return (
    <section className="hero" style={{ minHeight: '60vh' }}>
      <div className="container">
        <div className="hero-inner" style={{ maxWidth: 760 }}>
          <div className="status-pill">
            <span className="status-dot" />
            CYB-1 · Online · API v1
          </div>
          <h1 className="hero-title" style={{ fontSize: 'clamp(36px, 6vw, 72px)' }}>
            Developer platform.<br />
            <em>Every modality.</em><br />
            <span className="accent">One key.</span>
          </h1>
          <p className="hero-sub">
            Chat, vision, voice, embeddings, and reasoning — unified under one API key,
            one auth surface, one billing dashboard. Sub-300ms p50 across four African regions.
          </p>
          <div className="hero-cta">
            <Link to="/signup" className="btn btn-primary btn-lg">
              Generate API key
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6h8m0 0L6 2m4 4L6 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </Link>
            <Link to="/docs" className="btn btn-ghost btn-lg">Read the docs</Link>
          </div>
        </div>
      </div>
      <div className="hero-meta">
        <div className="col"><span>Model</span><strong>CYB-1 / Pre-release</strong></div>
        <div className="col"><span>Latency p50</span><strong>284ms</strong></div>
        <div className="col"><span>Context window</span><strong>200,000 tokens</strong></div>
        <div className="col"><span>Regions</span><strong>LAG-01 · NBO-02 · JNB-01</strong></div>
        <div className="col"><span>SDK languages</span><strong>Node.js · Python · REST</strong></div>
      </div>
    </section>
  );
}

function QuickStart() {
  const [tab, setTab] = useState('python');
  const code = {
    python: `from cybernate import Client

client = Client(api_key="cyb_•••")

response = client.chat.complete(
  model="cyb-1",
  messages=[{
    "role": "user",
    "content": "Translate to Yoruba: Hello, how are you?"
  }],
  region="lagos",
)

# Streaming, vision, voice — same client.
print(response.message.content)`,

    typescript: `import { Cybernate } from "@cybernate/sdk";

const cyb = new Cybernate({ apiKey: process.env.CYB_KEY });

const stream = await cyb.chat.stream({
  model: "cyb-1",
  messages: [{ role: "user", content: prompt }],
  region: "nairobi",
});

for await (const chunk of stream)
  process.stdout.write(chunk.delta);`,

    curl: `curl https://api.cybernate.ai/v1/chat/complete \\
  -H "Authorization: Bearer $CYB_KEY" \\
  -H "X-Region: lagos" \\
  -d '{"model":"cyb-1","messages":[
    {"role":"user","content":"Sannu, ina kwana?"}
  ]}'`,
  };

  return (
    <section className="section" id="quickstart">
      <div className="container">
        <div className="line-mark">01 / QUICK START</div>
        <div className="code-section">
          <div>
            <h2 className="section-title">One API.<br /><em>Every modality.</em></h2>
            <p className="section-desc" style={{ marginTop: 20, marginBottom: 32 }}>
              Chat, vision, voice, embeddings, and tools — all under one client, one auth surface, one billing dashboard.
            </p>
            <div style={{ marginBottom: 28 }}>
              {[
                ['Install Node.js SDK', 'npm install cybernate-ai'],
                ['Install Python SDK', 'pip install cybernate'],
              ].map(([label, cmd]) => (
                <div key={label} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, color: 'var(--fg-dim)', minWidth: 140 }}>{label}</span>
                  <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--signal)', background: 'var(--bg-1)', padding: '4px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }}>{cmd}</code>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Link to="/docs" className="btn btn-ghost">Full documentation</Link>
              <Link to="/signup" className="btn btn-primary">Get a free key</Link>
            </div>
          </div>
          <div className="code-window">
            <div className="code-titlebar">
              <div className="code-tabs">
                {['python', 'typescript', 'curl'].map(t => (
                  <button key={t} className={`code-tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
                    {t.toUpperCase()}
                  </button>
                ))}
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>cyb-1 · v1</span>
            </div>
            <div className="code-body">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{code[tab]}</pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ApiReference() {
  const endpoints = [
    {
      method: 'POST',
      path: '/v1/chat/complete',
      desc: 'Complete a chat conversation. Supports streaming, system prompts, tool use, and structured outputs.',
      tags: ['Streaming', 'Tool use', 'Structured output'],
    },
    {
      method: 'POST',
      path: '/v1/vision/analyze',
      desc: 'Analyze images, video frames, documents, and satellite tiles. Returns structured detection results with confidence scores.',
      tags: ['Object detection', 'OCR', 'Medical imaging'],
    },
    {
      method: 'POST',
      path: '/v1/speech/transcribe',
      desc: 'Transcribe audio in 18 African languages. Supports voice notes, phone calls, and real-time streaming.',
      tags: ['18 languages', 'Real-time', 'Clinical domain'],
    },
    {
      method: 'POST',
      path: '/v1/embeddings',
      desc: 'Generate high-dimensional vector embeddings for semantic search, similarity, and recommendation systems.',
      tags: ['cy-embed-v2', '1536-dim', 'Multilingual'],
    },
    {
      method: 'POST',
      path: '/v1/reasoning/run',
      desc: 'Run multi-step reasoning tasks with tool use. Supports chain-of-thought, agents, and autonomous task completion.',
      tags: ['Agents', 'Multi-step', 'Tool use'],
    },
    {
      method: 'GET',
      path: '/v1/models',
      desc: 'List all available models, their capabilities, supported languages, and regional availability.',
      tags: ['CYB-1', 'cy-embed-v2', 'Regions'],
    },
  ];

  const methodColor = { POST: 'var(--accent)', GET: 'var(--signal)', DELETE: '#FF6B6B' };

  return (
    <section className="section" id="api-reference">
      <div className="container">
        <div className="line-mark">02 / API REFERENCE</div>
        <div className="section-head">
          <div><h2 className="section-title">REST endpoints.<br /><em>Fully documented.</em></h2></div>
          <p className="section-desc">Base URL: <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg)', background: 'var(--bg-1)', padding: '2px 8px', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }}>api.cybernate.ai</code></p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--line)', border: '1px solid var(--line)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
          {endpoints.map(ep => (
            <div key={ep.path} style={{ background: 'var(--bg)', padding: '24px 28px', display: 'grid', gridTemplateColumns: '200px 1fr', gap: 24, alignItems: 'start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: methodColor[ep.method] || 'var(--fg-mid)', letterSpacing: '0.1em', fontWeight: 600 }}>{ep.method}</span>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg)', letterSpacing: '-0.01em' }}>{ep.path}</div>
              </div>
              <div>
                <p style={{ fontSize: 14, color: 'var(--fg-mid)', lineHeight: 1.55, marginBottom: 12 }}>{ep.desc}</p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {ep.tags.map(tag => (
                    <span key={tag} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', background: 'var(--bg-1)', border: '1px solid var(--line)', padding: '2px 8px', borderRadius: 'var(--r-full, 999px)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Link to="/docs" className="btn btn-ghost">View full API documentation →</Link>
        </div>
      </div>
    </section>
  );
}

function TechnicalCapabilities() {
  const [active, setActive] = useState('language');

  const capabilities = [
    {
      id: 'language',
      label: 'Language & Chat',
      desc: 'Chat, summarization, translation, and generation — optimized for African languages and contexts.',
      accent: '#80DEEA',
      demo: `// Chat in Swahili, Hausa, Yoruba, Igbo...

const ai = new CybernateAI(API_KEY);

const reply = await ai.chat({
  message: "Habari, nikusaidie?",
  language: "sw",
  context: "customer_support"
});

// → { message: "...", confidence: 0.98 }`,
    },
    {
      id: 'vision',
      label: 'Vision & Detection',
      desc: 'Real-time object detection, scene understanding, and visual analysis on video streams and images.',
      accent: '#C792EA',
      demo: `// Analyze video streams in real-time

const watcher = await ai.vision.watch({
  streamUrl: "rtmp://cam-01.site.ng",
  detect: ["person", "weapon", "vehicle"],
  threshold: 0.82
});

ai.on("detection", (e) => {
  console.log(e.objects, e.confidence);
});`,
    },
    {
      id: 'embeddings',
      label: 'Embeddings & Search',
      desc: 'Convert text and documents into high-dimensional vectors for semantic search and recommendations.',
      accent: '#FFD580',
      demo: `// Generate embeddings for semantic search

const vectors = await ai.embed({
  texts: ["M-Pesa transfer", "mobile money"],
  model: "cy-embed-v2"
});

const results = await ai.search({
  query: vectors[0],
  topK: 10
});`,
    },
    {
      id: 'reasoning',
      label: 'Reasoning & Agents',
      desc: 'Chain-of-thought reasoning, autonomous agents, and multi-step task completion for enterprise workflows.',
      accent: '#6BFFB8',
      demo: `// Multi-step agent reasoning

const agent = ai.agent({
  task: "Analyze fraud patterns in Q1 data",
  tools: ["database", "reports", "alerts"],
  maxSteps: 12
});

const result = await agent.run();
// → { findings: [...], confidence: 0.94 }`,
    },
    {
      id: 'speech',
      label: 'Voice & Speech',
      desc: 'Real-time speech-to-text in African languages, with sub-400ms turn-taking for voice agents.',
      accent: '#FF6B6B',
      demo: `// Transcribe in Hausa, Yoruba, Swahili...

const transcript = await ai.speech.transcribe({
  audio: voiceNoteBuffer,
  language: "ha",  // Hausa
  domain: "clinical",
});

// → { text: "Maganin malaria...", confidence: 0.96 }`,
    },
  ];

  const cap = capabilities.find(c => c.id === active);

  return (
    <section className="section" id="capabilities">
      <div className="container">
        <div className="line-mark">03 / CAPABILITIES</div>
        <div className="section-head">
          <div><h2 className="section-title">Five intelligence engines.<br /><em>One platform.</em></h2></div>
          <p className="section-desc">All modalities under a single client, single auth, single billing surface.</p>
        </div>

        <div className="code-section" style={{ alignItems: 'start', gap: 40 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {capabilities.map(c => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                style={{
                  textAlign: 'left',
                  padding: '16px 20px',
                  borderRadius: 'var(--r-md)',
                  background: active === c.id ? 'var(--bg-1)' : 'transparent',
                  border: `1px solid ${active === c.id ? 'var(--line-2)' : 'transparent'}`,
                  transition: 'all 150ms',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: active === c.id ? c.accent : 'var(--fg)', marginBottom: 4, letterSpacing: '-0.01em' }}>{c.label}</div>
                <div style={{ fontSize: 13, color: 'var(--fg-dim)', lineHeight: 1.5 }}>{c.desc}</div>
              </button>
            ))}
          </div>

          <div className="code-window" style={{ flex: 1 }}>
            <div className="code-titlebar">
              <div style={{ display: 'flex', gap: 6 }}>
                {['#FF5F57', '#FEBC2E', '#28C840'].map(c => (
                  <div key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />
                ))}
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: cap.accent }}>{cap.label.toLowerCase().replace(' & ', '-').replace(' ', '-')}.js</span>
            </div>
            <div className="code-body">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', minHeight: 220 }}>{cap.demo}</pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RegionsAndLatency() {
  const regions = [
    { id: 'LAG-01', city: 'Lagos', country: 'Nigeria', latency: '~84ms', status: 'live', desc: 'West Africa primary cluster' },
    { id: 'NBO-02', city: 'Nairobi', country: 'Kenya', latency: '~96ms', status: 'live', desc: 'East Africa primary cluster' },
    { id: 'JNB-01', city: 'Johannesburg', country: 'South Africa', latency: '~112ms', status: 'live', desc: 'Southern Africa cluster' },
    { id: 'ACC-01', city: 'Accra', country: 'Ghana', latency: '~140ms', status: 'coming', desc: 'West Africa secondary — 2026 Q3' },
  ];

  return (
    <section className="section" id="regions">
      <div className="container">
        <div className="line-mark">04 / REGIONS & LATENCY</div>
        <div className="section-head">
          <div><h2 className="section-title">African infrastructure.<br /><em>Minimal latency.</em></h2></div>
          <p className="section-desc">Route requests to the nearest cluster with the <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>X-Region</code> header or SDK parameter.</p>
        </div>

        <div className="regions-grid">
          {regions.map(r => (
            <div key={r.id} style={{ background: 'var(--bg)', padding: '28px 24px' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: r.status === 'live' ? 'var(--signal)' : 'var(--fg-faint)', boxShadow: r.status === 'live' ? '0 0 8px var(--signal)' : 'none' }} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{r.status === 'live' ? 'Live' : 'Coming soon'}</span>
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.1em', marginBottom: 6 }}>{r.id}</div>
              <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 4 }}>{r.city}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-dim)', marginBottom: 16 }}>{r.country}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 500, letterSpacing: '-0.04em', color: r.status === 'live' ? 'var(--fg)' : 'var(--fg-faint)', marginBottom: 4 }}>{r.latency}</div>
              <div style={{ fontSize: 11, color: 'var(--fg-dim)' }}>local p50</div>
            </div>
          ))}
        </div>

        <div className="latency-stats-grid">
          {[
            ['p50 globally', '284ms', 'Median across all regions'],
            ['p95 globally', '412ms', '95th percentile latency'],
            ['uptime', '99.95%', 'Measured over last 90 days'],
          ].map(([label, value, desc]) => (
            <div key={label} style={{ background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', padding: '20px 24px' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-dim)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 500, letterSpacing: '-0.04em', color: 'var(--fg)', marginBottom: 4 }}>{value}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-dim)' }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TechnicalPricing() {
  const plans = [
    {
      name: 'Developer', price: '$0',
      desc: '$50 in free credits. No card required. For prototypes and personal projects.',
      feats: [
        '1M tokens free / month',
        'All modalities (Language, Vision, Speech, Embeddings)',
        'Rate limit: 60 rpm',
        'Community support',
        'Access to all regions',
      ],
      cta: 'Get free key',
      featured: false,
    },
    {
      name: 'Build',
      badge: 'Recommended',
      price: '$0.40',
      unit: '/ 1M input tokens',
      desc: 'For teams shipping to production. Usage-based, transparent, no minimums.',
      feats: [
        '$0.40 input · $1.60 output per 1M tokens',
        '5,000 rpm',
        'Regional routing (LAG / NBO / JNB)',
        'Voice + Vision + Embeddings included',
        'Email & Slack support',
        'Streaming + tool use',
      ],
      cta: 'Start with Build',
      featured: true,
    },
    {
      name: 'Sovereign', price: 'Custom',
      desc: 'Dedicated GPU capacity, in-country data residency, and fine-tuning on your own data.',
      feats: [
        'Reserved GPU cluster (40–400 nodes)',
        'Custom model fine-tuning on your data',
        'NDPR / POPIA / Kenya DPA audit pack',
        '99.95% SLA · 24/7 dedicated support',
        'Named solutions engineer',
        'On-premise deployment option',
      ],
      cta: 'Talk to us',
      href: 'mailto:sales@cybernate.ai',
      featured: false,
    },
  ];

  return (
    <section className="section" id="dev-pricing">
      <div className="container">
        <div className="line-mark">05 / TECHNICAL PRICING</div>
        <div className="section-head">
          <div><h2 className="section-title">Pay for what runs.<br /><em>Nothing else.</em></h2></div>
          <p className="section-desc">Free credits to start. Token-metered usage. Enterprise commits with dedicated capacity and SLA.</p>
        </div>
        <div className="plans">
          {plans.map(p => (
            <div key={p.name} className={`plan${p.featured ? ' featured' : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="plan-name">{p.name}</div>
                {p.badge && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--accent)', background: 'rgba(139,124,255,0.1)', border: '1px solid rgba(139,124,255,0.2)', padding: '2px 8px', borderRadius: '999px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{p.badge}</span>
                )}
              </div>
              <div className="plan-price">
                {p.price}
                {p.unit && <span className="unit">{p.unit}</span>}
              </div>
              <div className="plan-desc">{p.desc}</div>
              <ul className="plan-feats">
                {p.feats.map(f => (
                  <li key={f}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6 L5 9 L10 3" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
              {p.href
                ? <a href={p.href} className="btn btn-ghost">{p.cta}</a>
                : <Link to="/signup" className={`btn ${p.featured ? 'btn-primary' : 'btn-ghost'}`}>{p.cta}</Link>
              }
            </div>
          ))}
        </div>
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--fg-dim)', marginTop: 24 }}>
          All plans include SSL/TLS, 99.5% uptime SLA on free tier, and access to Node.js + Python SDKs.
          {' '}<a href="mailto:info@cybernate.ai" style={{ color: 'var(--accent)' }}>Questions? Talk to us.</a>
        </p>
      </div>
    </section>
  );
}

function DevCTA() {
  return (
    <section className="section cta-section">
      <div className="container">
        <div className="cta-frame">
          <div className="cta-rule" />
          <div className="cta-grid">
            <div>
              <div className="kicker"><span className="kicker-num">06</span> Get started</div>
              <h2 className="cta-headline">Your first key<br /><em>takes&nbsp;thirty&nbsp;seconds.</em></h2>
              <p className="cta-lede">
                Sign up. Pick a region. Start sending requests. We'll spot you{' '}
                <span style={{ color: 'var(--fg)', fontWeight: 500 }}>$50 in credits</span> to make
                sure the first thing you ship doesn't cost you anything.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/signup" className="btn btn-primary btn-lg">
                  Generate a key
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6h8m0 0L6 2m4 4L6 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                </Link>
                <Link to="/chat" className="btn btn-ghost btn-lg">Try in chat first</Link>
              </div>
            </div>
            <div className="cta-right">
              <div className="cta-spec">
                {[
                  ['CREDIT', '$50.00'],
                  ['CARD REQUIRED', 'no'],
                  ['DEMO CALL', 'no'],
                  ['REGIONS LIVE', 'LAG · NBO · JNB'],
                  ['LATENCY p50', '~284ms'],
                  ['SDK LANGUAGES', 'Node.js · Python · REST'],
                ].map(([k, v]) => (
                  <div className="spec-row" key={k}>
                    <span className="spec-k">{k}</span>
                    <span className="spec-v">{v}</span>
                  </div>
                ))}
              </div>
              <div className="cta-pulse">
                <div className="pulse-dot" />
                <span>cluster healthy · accepting traffic</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
